'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { useCurrentUser } from '@/lib/hooks/use-auth'
import { canAccess, routePermissions } from '@/lib/permissions'
import type { UserRole } from '@okra/shared'

type NavItem = { href: string; label: string; icon: string; newTab?: boolean }

const navSections: NavItem[][] = [
  [
    { href: '/dashboard', label: 'Tableau de bord', icon: '⊞' },
  ],
  [
    { href: '/sales', label: 'Ventes', icon: '🧾' },
    { href: '/clients', label: 'Clients', icon: '👥' },
    { href: '/credit/overdue', label: 'Impayés', icon: '⚠' },
    { href: '/stats', label: 'Statistiques', icon: '📊' },
  ],
  [
    { href: '/cash', label: 'Ma Caisse', icon: '💰' },
  ],
  [
    { href: '/purchases', label: 'Achats', icon: '🛒' },
    { href: '/suppliers', label: 'Fournisseurs', icon: '🚚' },
  ],
  [
    { href: '/stock', label: 'Stock', icon: '📦' },
  ],
  [
    { href: '/periods', label: 'Périodes', icon: '📅' },
    { href: '/exports', label: 'Exports', icon: '📤' },
    { href: '/accounts', label: 'Comptes', icon: '🏦' },
  ],
  [
    { href: '/pricing', label: 'Gestion des prix', icon: '€' },
    { href: '/products', label: 'Produits', icon: '🏷' },
    { href: '/agencies', label: 'Agences', icon: '🏢' },
    { href: '/users', label: 'Utilisateurs', icon: '👤' },
    { href: '/settings', label: 'Paramètres', icon: '⚙️' },
  ],
  [
    { href: '/docs', label: 'Documentation', icon: '📖', newTab: true },
  ],
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const pathname = usePathname()
  const user = useCurrentUser()

  const visibleSections = navSections
    .map((section) =>
      section.filter(({ href }) => {
        const required = routePermissions[href]
        if (!required) return true
        if (!user) return false
        return canAccess(user.role as UserRole, required, href)
      }),
    )
    .filter((section) => section.length > 0)

  return (
    <aside
      className={cn(
        // Mobile : drawer fixe, hors-écran par défaut
        'fixed inset-y-0 left-0 z-50 flex h-full w-72 flex-col border-r bg-card transition-transform duration-300 ease-in-out',
        // Desktop : statique, toujours visible
        'md:static md:w-56 md:z-auto md:translate-x-0 md:transition-none',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
        <span className="flex items-center gap-2 font-bold text-lg tracking-tight">
          <img src="/tinda_favicon.svg" alt="" className="h-6 w-6" />
          Tinda
        </span>
        <button
          onClick={onClose}
          className="md:hidden flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
          aria-label="Fermer le menu"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="2" y1="2" x2="14" y2="14" />
            <line x1="14" y1="2" x2="2" y2="14" />
          </svg>
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {visibleSections.map((section, idx) => (
          <div key={idx}>
            {idx > 0 && <hr className="my-2 mx-3 border-border" />}
            <ul className="space-y-0.5 px-2">
              {section.map(({ href, label, icon, newTab }) => {
                const active = !newTab && (pathname === href || pathname.startsWith(href + '/'))
                return (
                  <li key={href}>
                    <Link
                      href={href}
                      onClick={onClose}
                      target={newTab ? '_blank' : undefined}
                      rel={newTab ? 'noopener noreferrer' : undefined}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      <span className="text-base leading-none">{icon}</span>
                      <span className="flex-1">{label}</span>
                      {newTab && <span className="text-xs opacity-60">↗</span>}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  )
}
